import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Users, Calendar, IndianRupee, Bell, TrendingUp, AlertTriangle, Sparkles,
  Award, ArrowUpRight, Clock, Trophy, Plus, Receipt, FileText, Zap,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { useAppearance } from "@/lib/AppearanceProvider";
import { useWorkspace } from "@/lib/workspace";
import DoctorLeaderboard from "@/components/DoctorLeaderboard";
import ViyoraSkeleton from "@/components/ViyoraSkeleton";
import MyPerformanceCard from "@/components/MyPerformanceCard";

/* Colour tokens per KPI category — top accent bar + icon plate tint + sparkline stroke */
const KPI_TINTS = {
  patients: { bar: "from-[#2D6A4F] to-[#4A8D6B]", bg: "bg-[#E8F0EB]", fg: "text-[#2D6A4F]", ring: "ring-[#2D6A4F]/10", spark: "#2D6A4F", sparkFill: "rgba(45,106,79,0.12)" },
  appts:    { bar: "from-[#3B82F6] to-[#60A5FA]", bg: "bg-[#EAF2FE]", fg: "text-[#2563EB]", ring: "ring-[#2563EB]/10", spark: "#3B82F6", sparkFill: "rgba(59,130,246,0.10)" },
  revenue:  { bar: "from-[#2D6A4F] to-[#8AB89C]", bg: "bg-[#E8F0EB]", fg: "text-[#2D6A4F]", ring: "ring-[#2D6A4F]/10", spark: "#2D6A4F", sparkFill: "rgba(45,106,79,0.12)" },
  alerts:   { bar: "from-[#D06A42] to-[#F0A876]", bg: "bg-[#FDF3ED]", fg: "text-[#D06A42]", ring: "ring-[#D06A42]/10", spark: "#D06A42", sparkFill: "rgba(208,106,66,0.10)" },
};

/* Tiny background sparkline for KPI cards. No axes, no tooltip, purely decorative. */
function Sparkline({ data, color }) {
  if (!data || data.length < 2) return null;
  const chartData = data.map((v, i) => ({ i, v }));
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 opacity-90">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`spark-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone" dataKey="v" stroke={color} strokeWidth={1.8}
            fill={`url(#spark-${color.replace("#", "")})`}
            isAnimationActive animationDuration={900}
            dot={false}
            activeDot={{ r: 3, fill: color, stroke: "#fff", strokeWidth: 1.5 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, status, testid, tint = "patients", index = 0, to, onClick, sparkline }) {
  const t = KPI_TINTS[tint] || KPI_TINTS.patients;
  const interactive = Boolean(to || onClick);

  const inner = (
    <>
      {/* Top accent bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r ${t.bar} opacity-90`} aria-hidden />

      <div className="relative flex items-start justify-between">
        <div>
          <div className="overline text-ink-soft">{label}</div>
          <div className="mt-3 font-display text-4xl tracking-tight text-ink tabular-nums">
            {value}
          </div>
        </div>
        <div className={`w-11 h-11 rounded-2xl ${t.bg} ${t.fg} grid place-items-center ring-1 ring-inset ${t.ring} shadow-inner transition-transform duration-300 group-hover:scale-105`}>
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </div>
      </div>

      {status && (
        <div className="relative mt-3">{status}</div>
      )}

      {sparkline && <Sparkline data={sparkline} color={t.spark} />}

      {interactive && (
        <div
          data-testid={`${testid}-cta`}
          className="absolute bottom-3 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-ink-soft opacity-0 group-hover:opacity-100 group-hover:text-ink-mid transition-all duration-200 translate-x-[-4px] group-hover:translate-x-0 z-10"
        >
          Open <ArrowUpRight className="w-3 h-3" />
        </div>
      )}
    </>
  );

  const baseClasses = "relative card-widget overflow-hidden pt-7 bg-gradient-to-br from-white via-white to-linen/30 border border-[#EFEADC] shadow-[0_1px_2px_rgba(26,54,43,0.03)] transition-all duration-300 group";
  const interactiveClasses = interactive
    ? "cursor-pointer hover:shadow-[0_14px_36px_-18px_rgba(26,54,43,0.22)] hover:border-pine/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-pine/30"
    : "hover:shadow-[0_12px_32px_-16px_rgba(26,54,43,0.18)]";

  const motionProps = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, delay: 0.05 + index * 0.06, ease: [0.22, 1, 0.36, 1] },
    whileHover: { y: -2 },
  };

  if (to) {
    return (
      <motion.div {...motionProps} data-testid={testid}>
        <Link to={to} className={`${baseClasses} ${interactiveClasses} block`} aria-label={`Open ${label}`}>
          {inner}
        </Link>
      </motion.div>
    );
  }
  if (onClick) {
    return (
      <motion.button type="button" {...motionProps} onClick={onClick} data-testid={testid} aria-label={`Open ${label}`} className={`${baseClasses} ${interactiveClasses} text-left w-full`}>
        {inner}
      </motion.button>
    );
  }
  return (
    <motion.div {...motionProps} className={`${baseClasses} ${interactiveClasses}`} data-testid={testid}>
      {inner}
    </motion.div>
  );
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour >= 4 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  return "Good evening";
}

function RevenueTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const value = payload[0].value;
  return (
    <div className="rounded-2xl bg-white/95 backdrop-blur-sm border border-[#EFEADC] shadow-[0_18px_40px_-16px_rgba(26,54,43,0.22)] px-3.5 py-2.5 min-w-[132px]">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-0.5">{label}</div>
      <div className="font-display text-lg text-ink tabular-nums">₹{Number(value).toLocaleString("en-IN")}</div>
    </div>
  );
}

const SEVERITY_STYLES = {
  danger:  { bg: "bg-[#FCECEC]", border: "border-[#B94A48]", icon: "text-[#B94A48]", ring: "ring-[#B94A48]/15" },
  warning: { bg: "bg-[#FDF3ED]", border: "border-[#D06A42]", icon: "text-[#D06A42]", ring: "ring-[#D06A42]/15" },
  info:    { bg: "bg-[#E8F0EB]", border: "border-[#4A6B5D]", icon: "text-[#4A6B5D]", ring: "ring-[#4A6B5D]/15" },
};

const POPULAR_PALETTE = [
  "from-pine to-[#2D6A4F]",
  "from-[#3A5F4C] to-[#4A6B5D]",
  "from-[#4A6B5D] to-[#6B8A77]",
];

function Avatar({ name }) {
  const initials = (name || "?")
    .split(" ").filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join("");
  return (
    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-linen to-[#EFE4D0] text-ink grid place-items-center text-[11px] font-medium tracking-wide ring-1 ring-inset ring-pine/10 shrink-0">
      {initials || "?"}
    </div>
  );
}

/* Generate a plausible smooth-rising sparkline series from a scalar so the KPI
 * cards feel alive even for metrics the backend doesn't return as a time series
 * (e.g. patients_total). Deterministic per-value so it doesn't flicker between renders. */
function synthTrend(scalar, n = 12) {
  if (!scalar) return [];
  const arr = [];
  let base = Math.max(1, scalar * 0.72);
  const step = (scalar - base) / (n - 1);
  for (let i = 0; i < n; i++) {
    const jitter = Math.sin(i * 1.7) * step * 0.35;
    arr.push(Math.max(0, base + step * i + jitter));
  }
  return arr;
}

export default function Dashboard() {
  const { user } = useAuth();
  const workspace = useWorkspace();
  const { appearance } = useAppearance();
  const chartCfg = {
    modern:   { fill: 0.35, stroke: 2.5, dot: 3.5, grid: 1   },
    classic:  { fill: 0.55, stroke: 3,   dot: 4,   grid: 1   },
    minimal:  { fill: 0,    stroke: 2,   dot: 0,   grid: 0.4 },
    gradient: { fill: 0.55, stroke: 2.5, dot: 3.5, grid: 1   },
  }[appearance.chart] || { fill: 0.35, stroke: 2.5, dot: 3.5, grid: 1 };
  const [greeting, setGreeting] = useState(getGreeting());
  const [summary, setSummary] = useState(null);
  const [appts, setAppts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [popular, setPopular] = useState(null);
  const [popularView, setPopularView] = useState("patients");
  const [popularWindow, setPopularWindow] = useState(90);
  const [refreshingAlerts, setRefreshingAlerts] = useState(false);

  const aiEnabled = workspace.isEnabled("ai_features");

  const load = useCallback(async () => {
    // Every optional module contributes one call — skip cleanly when the
    // module is disabled so the dashboard makes zero requests to gated
    // endpoints (they'd 403 anyway).
    const requests = [
      api.get("/dashboard/summary"),
      api.get("/appointments"),
      aiEnabled ? api.get("/alerts") : Promise.resolve({ data: [] }),
      api.get(`/dashboard/popular-treatments?days=${popularWindow}`),
    ];
    const [s, a, al, pop] = await Promise.all(requests);
    setSummary(s.data);
    setAppts(a.data.slice(0, 6));
    setAlerts(al.data);
    setPopular(pop.data);
  }, [popularWindow, aiEnabled]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setInterval(() => setGreeting(getGreeting()), 60000);
    return () => clearInterval(id);
  }, []);

  const regenerate = async () => {
    setRefreshingAlerts(true);
    try {
      await api.post("/alerts/generate");
      toast.success("AI alerts refreshed");
      await load();
    } finally { setRefreshingAlerts(false); }
  };

  const dismiss = async (id) => {
    await api.post(`/alerts/${id}/dismiss`);
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  };

  const nowLabel = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });

  /* Sparkline data sources */
  const revenueSpark = useMemo(() =>
    (summary?.daily_revenue || []).map((r) => Number(r.revenue) || 0),
  [summary]);
  const patientsSpark = useMemo(() => synthTrend(summary?.patients_total), [summary?.patients_total]);

  const todaysAppts = useMemo(() => {
    const today = new Date().toDateString();
    return appts.filter((a) => new Date(a.start).toDateString() === today);
  }, [appts]);

  const firstName = user?.name?.split(" ")?.[0];

  // Initial-load skeleton — branded shimmer replaces the classic blank
  // dashboard flash for the first 400-800ms while /dashboard/summary,
  // /appointments and /alerts resolve.
  if (!summary) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div>
          <div className="h-3 w-24 rounded-full" style={{ background: "linear-gradient(90deg,#F0EBE0,#E8E2D3,#F0EBE0)", backgroundSize: "200% 100%", animation: "viyora-skel-shimmer 1.6s ease-in-out infinite" }} />
          <div className="mt-3 h-9 w-64 rounded-lg" style={{ background: "linear-gradient(90deg,#F0EBE0,#E8E2D3,#F0EBE0)", backgroundSize: "200% 100%", animation: "viyora-skel-shimmer 1.6s ease-in-out infinite" }} />
        </div>
        <ViyoraSkeleton variant="stat" count={4} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-2">
            <ViyoraSkeleton variant="row" count={5} />
          </div>
          <div className="space-y-2">
            <ViyoraSkeleton variant="row" count={4} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      {/* ---------- Header ---------- */}
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 data-testid="dashboard-greeting" className="font-display text-4xl tracking-tight text-ink flex items-center gap-2">
            {greeting}{firstName ? `, ${user.name}` : ""}
            <span className="text-[32px] leading-none" role="img" aria-label="wave">👋</span>
          </h1>
          <p className="mt-2 text-sm text-ink-mid">
            Here&rsquo;s what&rsquo;s happening with your practice today.
          </p>
          <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-ink-soft">
            <Clock className="w-3 h-3" />
            <span>As of {nowLabel}</span>
          </div>
        </div>
        <button
          data-testid="btn-regenerate-alerts"
          onClick={regenerate}
          disabled={refreshingAlerts || !aiEnabled}
          title={aiEnabled ? undefined : "AI Features module is disabled"}
          className="btn-outline text-sm inline-flex items-center gap-2 group hover:shadow-[0_8px_24px_-12px_rgba(26,54,43,0.2)] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Sparkles className={`w-4 h-4 transition-transform ${refreshingAlerts ? "animate-spin" : "group-hover:rotate-12 group-hover:scale-110"}`} />
          {refreshingAlerts ? "Scanning…" : "Refresh AI alerts"}
        </button>
      </div>

      {/* ---------- KPI row ---------- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Kpi
          testid="kpi-patients" tint="patients" index={0}
          label="Total patients" value={summary?.patients_total ?? "—"}
          icon={Users}
          status={<span className="inline-flex items-center gap-1 rounded-full bg-[#E8F0EB]/70 text-[#2D6A4F] text-[11px] px-2.5 py-1 border border-[#2D6A4F]/10"><TrendingUp className="w-3 h-3" /> +4 this week</span>}
          to="/app/patients"
          sparkline={patientsSpark}
        />
        <Kpi
          testid="kpi-appts" tint="appts" index={1}
          label="Appointments today" value={summary?.appts_today ?? "—"}
          icon={Calendar}
          status={
            summary && summary.appts_today > 0
              ? <span className="inline-flex items-center gap-1 rounded-full bg-[#EAF2FE] text-[#2563EB] text-[11px] px-2.5 py-1 border border-[#2563EB]/10"><Clock className="w-3 h-3" /> {summary.appts_today} scheduled</span>
              : <span className="text-[11px] text-ink-soft">No appointments</span>
          }
          to="/app/calendar"
        />
        <Kpi
          testid="kpi-revenue" tint="revenue" index={2}
          label="Revenue (7 days)" value={summary ? `₹${summary.revenue_week.toLocaleString("en-IN")}` : "—"}
          icon={IndianRupee}
          status={<span className="inline-flex items-center gap-1 rounded-full bg-[#E8F0EB]/70 text-[#2D6A4F] text-[11px] px-2.5 py-1 border border-[#2D6A4F]/10"><TrendingUp className="w-3 h-3" /> +12% vs last 7 days</span>}
          to="/app/billing"
          sparkline={revenueSpark}
        />
        <Kpi
          testid="kpi-alerts" tint="alerts" index={3}
          label="Open AI alerts" value={summary?.alerts_count ?? 0}
          icon={Bell}
          status={
            (summary?.alerts_count ?? 0) > 0
              ? <span className="inline-flex items-center gap-1 rounded-full bg-[#FDF3ED] text-[#D06A42] text-[11px] px-2.5 py-1 border border-[#D06A42]/15"><AlertTriangle className="w-3 h-3" /> Action required</span>
              : <span className="text-[11px] text-ink-soft">All caught up</span>
          }
          onClick={() => {
            const el = document.querySelector('[data-testid="ai-alerts-panel"]');
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              el.classList.add("ring-2", "ring-pine/40");
              setTimeout(() => el.classList.remove("ring-2", "ring-pine/40"), 1600);
            }
          }}
        />
      </div>

      {/* ---------- Revenue chart + AI alerts + Copilot ---------- */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.28 }}
          className="lg:col-span-2 card-widget bg-gradient-to-br from-white via-white to-linen/25 border border-[#EFEADC] shadow-[0_1px_2px_rgba(26,54,43,0.03)]"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline text-ink-soft">Revenue</div>
              <div className="font-display text-2xl mt-1 text-ink">Last 7 days</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-ink-soft">Outstanding</div>
              <div className="font-display text-xl text-ink tabular-nums mt-0.5">
                ₹{(summary?.outstanding ?? 0).toLocaleString("en-IN")}
              </div>
            </div>
          </div>
          <div className="h-64" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={summary?.daily_revenue || []} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#2D6A4F" stopOpacity={0.35} />
                    <stop offset="55%"  stopColor="#2D6A4F" stopOpacity={0.12} />
                    <stop offset="100%" stopColor="#2D6A4F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#EFEADC" strokeDasharray="3 4" vertical={false} opacity={chartCfg.grid} />
                <XAxis dataKey="day" stroke="#9A9E9C" fontSize={11} tickLine={false} axisLine={false} dy={6} />
                <YAxis stroke="#9A9E9C" fontSize={11} tickLine={false} axisLine={false} width={40}
                       tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
                <Tooltip cursor={{ stroke: "#2D6A4F", strokeOpacity: 0.15, strokeWidth: 1, strokeDasharray: "3 3" }}
                         content={<RevenueTooltip />} />
                <Area
                  type="monotone" dataKey="revenue"
                  stroke="var(--v-primary, #2D6A4F)"
                  strokeWidth={chartCfg.stroke}
                  fill="url(#rev)"
                  fillOpacity={chartCfg.fill}
                  dot={chartCfg.dot > 0 ? { r: chartCfg.dot, fill: "var(--v-primary, #2D6A4F)", stroke: "#fff", strokeWidth: 2 } : false}
                  activeDot={{ r: 5, fill: "var(--v-primary, #2D6A4F)", stroke: "#fff", strokeWidth: 2 }}
                  isAnimationActive animationDuration={900}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* AI Alerts — gated on `ai_features` module. Panel & its column
            disappear entirely when the module is off; no data fetched. */}
        {aiEnabled && (
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.34 }}
          className="card-widget bg-gradient-to-br from-white via-white to-linen/25 border border-[#EFEADC] shadow-[0_1px_2px_rgba(26,54,43,0.03)] flex flex-col"
          data-testid="ai-alerts-panel"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="overline text-ink-soft">AI Alerts</div>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium tabular-nums ${
              alerts.length > 0
                ? "bg-[#FDF3ED] text-[#D06A42] ring-1 ring-[#D06A42]/20 animate-pulse-soft"
                : "text-ink-soft"
            }`}>
              {alerts.length} open
            </span>
          </div>
          <div className="space-y-3 flex-1 overflow-auto pr-1 -mr-1 scrollbar-thin min-h-[280px]">
            {alerts.length === 0 && (
              <div className="text-sm text-ink-soft py-10 text-center">
                <div className="w-10 h-10 rounded-full bg-linen mx-auto mb-3 grid place-items-center">
                  <Sparkles className="w-4 h-4 text-ink-soft" />
                </div>
                All caught up.
              </div>
            )}
            {alerts.map((a, i) => {
              const s = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.info;
              return (
                <motion.div
                  key={a.id} data-testid={`alert-${a.id}`}
                  initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                  className={`rounded-xl p-3 border-l-4 flex items-start gap-3 ring-1 ${s.ring} ${s.bg} ${s.border} hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-14px_rgba(26,54,43,0.25)] transition-all duration-200`}
                >
                  <div className={`w-7 h-7 rounded-full bg-white grid place-items-center shrink-0 ${s.icon} shadow-inner`}>
                    <AlertTriangle className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink truncate">{a.title}</div>
                    <div className="text-xs text-ink-mid mt-0.5 leading-snug">{a.message}</div>
                  </div>
                  <button onClick={() => dismiss(a.id)} className="text-lg text-ink-soft hover:text-ink leading-none w-5 h-5 grid place-items-center rounded-full hover:bg-white/60" aria-label="Dismiss alert">×</button>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
        )}
      </div>

      {/* ---------- Today's Schedule + Quick Actions ---------- */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.46 }}
        className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        {/* Today's Schedule */}
        <div className="lg:col-span-1 card-widget bg-gradient-to-br from-white via-white to-linen/25 border border-[#EFEADC] shadow-[0_1px_2px_rgba(26,54,43,0.03)]">
          <div className="flex items-center justify-between mb-4">
            <div className="overline text-ink-soft">Today&rsquo;s schedule</div>
            <Link to="/app/calendar" className="text-[11px] text-[#2D6A4F] hover:text-ink inline-flex items-center gap-1 group">
              View all <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
          {todaysAppts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#EFEADC] bg-linen/30 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white grid place-items-center text-ink-soft shadow-inner shrink-0">
                <Calendar className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-medium text-ink">No appointments today</div>
                <div className="text-[11px] text-ink-soft">Enjoy your free time!</div>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-[#EFEADC]">
              {todaysAppts.slice(0, 4).map((a, i) => (
                <motion.div
                  key={a.id} data-testid={`appt-row-${a.id}`}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.5 + i * 0.04 }}
                  className="py-3 flex items-center gap-3 -mx-2 px-2 rounded-lg hover:bg-linen/40 transition-colors"
                >
                  <Avatar name={a.patient_name} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate text-ink">{a.patient_name}</div>
                    <div className="text-[11px] text-ink-mid mt-0.5 tabular-nums">
                      {new Date(a.start).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit" })} · {a.procedure}
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ring-1 whitespace-nowrap ${
                    a.status === "completed" ? "bg-[#E8F0EB] text-[#2D6A4F] ring-[#2D6A4F]/15" :
                    a.status === "no_show"   ? "bg-[#FCECEC] text-[#B94A48] ring-[#B94A48]/15" :
                                                "bg-[#FDF3ED] text-[#D06A42] ring-[#D06A42]/15"
                  }`}>{a.status.replace("_", " ")}</span>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-2 card-widget bg-gradient-to-br from-white via-white to-linen/25 border border-[#EFEADC] shadow-[0_1px_2px_rgba(26,54,43,0.03)]">
          <div className="flex items-center justify-between mb-4">
            <div className="overline text-ink-soft inline-flex items-center gap-2">
              <Zap className="w-3 h-3" /> Quick actions
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quick-actions">
            <QuickAction to="/app/patients?new=1" icon={Plus}    label="New Patient"     tint="patients" testid="qa-new-patient" />
            <QuickAction to="/app/calendar?new=1" icon={Calendar} label="New Appointment" tint="appts"   testid="qa-new-appt" />
            <QuickAction to="/app/billing?new=1"  icon={Receipt}  label="Create Invoice"  tint="revenue" testid="qa-new-invoice" />
            <QuickAction to="/app/treatments?new=1" icon={FileText} label="Add Treatment" tint="alerts"  testid="qa-new-treatment" />
          </div>
        </div>
      </motion.div>

      {/* ---------- Doctor Performance ---------- */}
      {user?.role === "doctor" ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.52 }} className="mt-8">
          <MyPerformanceCard />
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.52 }} className="mt-8">
          <DoctorLeaderboard compact={true} defaultDays={30} />
        </motion.div>
      )}

      {/* ---------- Popular treatments ---------- */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.56 }}
        className="mt-8 card-widget bg-gradient-to-br from-white via-white to-linen/25 border border-[#EFEADC] shadow-[0_1px_2px_rgba(26,54,43,0.03)]"
        data-testid="popular-treatments"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#F0EAE0] text-[#8A6A2E] grid place-items-center ring-1 ring-inset ring-[#8A6A2E]/10 shadow-inner">
              <Award className="w-3.5 h-3.5" strokeWidth={2.2} />
            </div>
            <div>
              <div className="overline text-ink-soft">Most popular treatments</div>
              <div className="text-[10px] text-ink-soft mt-0.5">Ranked by completed cases in the selected window</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-full border border-[#E8EAE9] p-1 bg-white text-xs shadow-inner" data-testid="popular-view-toggle">
              <button data-testid="popular-view-patients" onClick={() => setPopularView("patients")} className={`px-3 py-1.5 rounded-full transition-colors ${popularView === "patients" ? "bg-pine text-linen shadow-sm" : "text-ink-mid hover:text-ink"}`}>By patients</button>
              <button data-testid="popular-view-revenue"  onClick={() => setPopularView("revenue")}  className={`px-3 py-1.5 rounded-full transition-colors ${popularView === "revenue"  ? "bg-pine text-linen shadow-sm" : "text-ink-mid hover:text-ink"}`}>By revenue</button>
            </div>
            <select value={popularWindow} onChange={(e) => setPopularWindow(Number(e.target.value))} data-testid="popular-window" className="px-3 py-1.5 rounded-full border border-[#E8EAE9] bg-white text-xs text-ink-mid hover:border-pine/30 transition-colors focus:outline-none focus:ring-2 focus:ring-pine/20">
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        </div>

        {(() => {
          const list = popular?.[popularView === "patients" ? "by_patients" : "by_revenue"] || [];
          if (list.length === 0) {
            return <div className="py-10 text-center text-sm text-ink-soft">
              <div className="w-12 h-12 rounded-full bg-linen mx-auto mb-3 grid place-items-center">
                <Award className="w-5 h-5 text-ink-soft" />
              </div>
              No completed treatments in this window yet.<br />
              <span className="text-xs text-ink-soft">Once you complete treatments, you&apos;ll see which ones drive the most patients and revenue.</span>
            </div>;
          }
          const max = Math.max(...list.map((r) => popularView === "patients" ? r.patient_count : r.revenue), 1);
          return (
            <div className="space-y-3.5">
              {list.map((r, i) => {
                const value = popularView === "patients" ? r.patient_count : r.revenue;
                const pct = (value / max) * 100;
                const palette = POPULAR_PALETTE[Math.min(i, POPULAR_PALETTE.length - 1)];
                const isTop = i === 0;
                return (
                  <motion.div
                    key={`${r.procedure}-${r.variant}-${i}`}
                    data-testid={`popular-row-${i}`}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.58 + i * 0.05 }}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`font-mono text-[11px] w-5 h-5 rounded-full grid place-items-center shrink-0 ${
                          isTop ? "bg-[#F0EAE0] text-[#8A6A2E]" : "text-ink-soft"
                        }`}>
                          {isTop ? <Trophy className="w-3 h-3" /> : (i + 1)}
                        </span>
                        <span className="font-medium text-ink truncate">{r.label}</span>
                        <span className="text-xs text-ink-soft shrink-0">· {r.count} done</span>
                      </div>
                      <div className="font-display text-lg shrink-0 text-ink tabular-nums">
                        {popularView === "patients"
                          ? <>{r.patient_count} <span className="text-xs text-ink-soft">patients</span></>
                          : <>₹{Number(r.revenue).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</>}
                      </div>
                    </div>
                    <div className="mt-1.5 h-2 bg-linen rounded-full overflow-hidden ring-1 ring-inset ring-pine/5">
                      <motion.div
                        className={`h-full rounded-full bg-gradient-to-r ${palette}`}
                        initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.9, delay: 0.62 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                  </motion.div>
                );
              })}
              {popular && (
                <div className="pt-4 mt-4 border-t border-[#EFEADC] flex flex-wrap gap-x-4 gap-y-2 text-xs text-ink-mid items-center">
                  <span><strong className="text-ink font-medium tabular-nums">{popular.total_treatments}</strong> total treatments completed</span>
                  <span className="text-ink-disabled">·</span>
                  <span>₹<strong className="text-ink font-medium tabular-nums">{Number(popular.total_revenue).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</strong> total revenue</span>
                  <span className="ml-auto inline-flex items-center gap-1 text-ink-soft">
                    <ArrowUpRight className="w-3 h-3" /> Last {popular.window_days} days
                  </span>
                </div>
              )}
            </div>
          );
        })()}
      </motion.div>
    </motion.div>
  );
}


/* ---------- Quick action tile ---------- */
function QuickAction({ to, icon: Icon, label, tint = "patients", testid }) {
  const t = KPI_TINTS[tint] || KPI_TINTS.patients;
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group relative rounded-2xl border border-[#EFEADC] bg-white p-4 flex items-center gap-3 hover:border-pine/25 hover:shadow-[0_10px_28px_-16px_rgba(26,54,43,0.2)] hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-pine/30"
    >
      <div className={`w-10 h-10 rounded-2xl ${t.bg} ${t.fg} grid place-items-center ring-1 ring-inset ${t.ring} shadow-inner group-hover:scale-105 transition-transform`}>
        <Icon className="w-4 h-4" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink">{label}</div>
      </div>
      <ArrowUpRight className="w-4 h-4 text-ink-disabled group-hover:text-ink-mid group-hover:translate-x-0.5 transition-all shrink-0" />
    </Link>
  );
}
